/* eslint-disable import/prefer-default-export */
import { DashboardWidgetData } from 'features/widget/types'
import { InsightCount } from 'schemas/action/InsightCountSchema'
import { InsightData } from 'schemas/action/InsightDataSchema'
import TableSortOption, { TableSortOptionStrings } from 'enums/TableSortOption'
import { ColSortingConfigs } from 'components/visualization/Table/types'
import InsightViewOption, { InsightViewOptionStrings } from 'enums/InsightViewOption'
import { MessageConst } from 'enums'
import { isRightToLeftText, RTL_MARK } from 'utils/rtlTextUtil'
import { TrendingWidgetsType, TrendingInsight } from '../../types'

const retrieveDataSet = (response: DashboardWidgetData<InsightCount>) =>
  response?.results?.[0]?.data?.insights?.[0]?.dataset?.[0]?.set

export const getNumberFormatter = () =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3
  })

/**
 * convert responses from runsearch to data displayed on table
 * @param searchResponses responses from runsearch
 * @param widgetSetting widget setting
 */
export const convertToTableData = (
  searchResponses: DashboardWidgetData<InsightCount>[],
  widgetSetting: TrendingWidgetsType
) => {
  const rowData =
    searchResponses
      ?.map((response) => {
        const dataSet = retrieveDataSet(response)
        return dataSet
          ?.map((item: InsightData) => {
            const aggregateValues = item!.aggregateResult?.values
            return {
              name: item!.name,
              trend: item!.trend,
              aggregateResult: item!.aggregateResult,
              lastPeriodValue: aggregateValues?.length
                ? parseInt(aggregateValues[aggregateValues.length - 1]!, 10)
                : 0,
              variants: item!.variants
            }
          })
          .filter((item: TrendingInsight) => {
            if (widgetSetting.minCount !== undefined && widgetSetting.minCount !== null) {
              return item.lastPeriodValue! >= widgetSetting.minCount
            }
            return true
          })
      })
      .flat() || []
  return rowData.slice(0, widgetSetting.topN!)
}

export const getDisplayText = (
  name: string,
  viewOption: InsightViewOptionStrings,
  emojiNameToDisplayMap: Record<string, string>
) => {
  switch (viewOption) {
    case InsightViewOption.EMOJIS_EMOJI:
    case InsightViewOption.EMOJIS_EMOTION:
    case InsightViewOption.EMOJIS_GROUP:
      return emojiNameToDisplayMap[name]
    default:
      if (isRightToLeftText(name) && name.charAt(0) === '#') {
        return RTL_MARK + name
      }
      return name
  }
}

export const getDisplayTrendScore = (value: any) => {
  if (Number.isNaN(Number(value))) {
    return MessageConst.EM_DASH
  }
  if (Object.is(value, -0)) {
    return 0
  }
  return getNumberFormatter().format(Math.round(value))
}

export const getMaxValue = (rowData: InsightData[]) => {
  let maxLastValue = 0

  for (let i = 0; i < rowData.length; i += 1) {
    const { values } = rowData[i].aggregateResult!

    if (values && values.length > 0) {
      const lastValue = parseInt(values[values.length - 1]!, 10)

      if (lastValue && lastValue > maxLastValue) {
        maxLastValue = lastValue
      }
    }
  }

  return maxLastValue
}

export const handleProgressDisplay = (progress: InsightData['trend']) => {
  if (typeof progress !== 'number') {
    return 0
  }
  return Math.round(progress)
}

export const getSortingConfigByTableSortOption = (
  tableSortOption: TableSortOptionStrings | undefined
): ColSortingConfigs<'name' | 'trend' | 'lastPeriodValue'> => {
  switch (tableSortOption) {
    case TableSortOption.ALPHABET_ASC:
      return {
        field: 'name',
        order: TableSortOption.ASCEND
      }
    case TableSortOption.ALPHABET_DSC:
      return {
        field: 'name',
        order: TableSortOption.DESCEND
      }
    case TableSortOption.PERCENT_ASC:
      return {
        field: 'trend',
        order: TableSortOption.ASCEND
      }
    case TableSortOption.PERCENT_DSC:
      return {
        field: 'trend',
        order: TableSortOption.DESCEND
      }
    case TableSortOption.LAST_ASC:
      return {
        field: 'lastPeriodValue',
        order: TableSortOption.ASCEND
      }
    case TableSortOption.LAST_DSC:
      return {
        field: 'lastPeriodValue',
        order: TableSortOption.DESCEND
      }
    default:
      return {
        field: 'trend',
        order: TableSortOption.DESCEND
      }
  }
}
